import { useState } from "react";
import { useGetFounderProfilePreview } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, Lock, Globe, Users, TrendingUp, FileText, Video } from "lucide-react";
import InfoTip from "@/components/info-tip";

export default function FounderPreview({ founderId }: { founderId: number }) {
  const [view, setView] = useState<"internal" | "founder" | "investor">("investor");

  const { data, isLoading } = useGetFounderProfilePreview(founderId, { view });

  return (
    <div className="space-y-6" data-testid="page-founder-preview">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Company Profile Preview</h1>
          <InfoTip content="Preview exactly what different audiences see about your company. 'Internal' shows everything (for ops team only). 'Founder' shows what you see. 'Investor' shows only what has been approved for investor visibility — this is the view investors get in their Intelligence Room." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          See how your company profile appears to different audiences
        </p>
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as any)}>
        <TabsList>
          <TabsTrigger value="internal" className="flex items-center gap-1">
            <Lock className="w-3 h-3" /> Internal
          </TabsTrigger>
          <TabsTrigger value="founder" className="flex items-center gap-1">
            <Users className="w-3 h-3" /> Founder View
          </TabsTrigger>
          <TabsTrigger value="investor" className="flex items-center gap-1">
            <Globe className="w-3 h-3" /> Investor View
          </TabsTrigger>
        </TabsList>

        <TabsContent value={view}>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-48" />
            </div>
          ) : !data ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Unable to load preview</CardContent></Card>
          ) : (
            <div className="space-y-6 mt-4">
              <Card className="border-t-4 border-t-primary">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px]">
                      <Eye className="w-3 h-3 mr-1" /> {view} view
                    </Badge>
                  </div>
                  <h2 className="text-xl font-serif font-bold">{data.company?.name}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{data.company?.sector} &middot; {data.company?.stage}</p>
                  {data.company?.description && (
                    <p className="text-sm mt-3">{data.company.description}</p>
                  )}
                  {data.thesis && (
                    <div className="mt-4 p-3 bg-muted rounded-md">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Investment Thesis</p>
                      <p className="text-sm">{data.thesis}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {data.founders?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-serif flex items-center gap-2">
                      <Users className="w-4 h-4" /> Founding Team
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-3">
                      {data.founders.map((f: any) => (
                        <div key={f.id} className="p-3 border rounded-md">
                          <p className="text-sm font-medium">{f.name}</p>
                          <p className="text-xs text-muted-foreground">{f.role}</p>
                          {f.bio && <p className="text-xs mt-2">{f.bio}</p>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {(data.tractionSnapshot?.length ?? 0) > 0 && data.tractionSnapshot && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-serif flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Traction Snapshot
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {data.tractionSnapshot.slice(0, 6).map((kpi: any) => (
                        <div key={kpi.id} className="p-3 bg-muted rounded-md text-center">
                          <p className="text-xs text-muted-foreground">{kpi.metricName}</p>
                          <p className="text-lg font-serif font-bold mt-1">{kpi.value}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {(data.documents?.length ?? 0) > 0 && data.documents && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-serif flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.documents.map((doc: any) => (
                        <div key={doc.id} className="flex items-center justify-between p-2 border rounded">
                          <span className="text-sm">{doc.title}</span>
                          <Badge variant="outline" className="text-[10px]">{doc.type}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {(data.mediaAssets?.length ?? 0) > 0 && data.mediaAssets && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-serif flex items-center gap-2">
                      <Video className="w-4 h-4" /> Media
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.mediaAssets.map((m: any) => (
                        <div key={m.id} className="flex items-center justify-between p-2 border rounded">
                          <span className="text-sm">{m.title}</span>
                          <Badge variant="outline" className="text-[10px]">{m.type}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
